import React from "react";
import type { OfferInputs, OfferResults, ScenarioPct } from "../../types/ambassadors";
import RevenueBudget from "./RevenueBudget";
import { count, pct, times, usd, yearsLabel } from "./format";

// The calculator's right column: live results, recomputed on every keystroke —
// what the promotion is worth and why, the proposed share, what it pays under
// each scenario against the market benchmark, the Year 1 check, and the year
// by year table. Admin-only: it shows the private assumptions (CPMs,
// multipliers) that Present mode leaves out.

type N = number | null;

const SCENARIOS: { key: keyof ScenarioPct; label: string }[] = [
  { key: "conservative", label: "Conservative" },
  { key: "base", label: "Base case" },
  { key: "upside", label: "Upside" },
];

const cpm = (v: N) => (v === null ? "—" : `$${count(v)}`);
const share = (part: N, whole: N) => (part === null || whole === null || whole <= 0 ? "—" : `${Math.round((part / whole) * 100)}%`);
const posts = (reels: N, stories: N) => `${count(reels)} Reels · ${count(stories)} Stories`;

interface Props {
  inputs: OfferInputs;
  results: OfferResults;
  /** The other saved offers, for the revenue-share budget (null while they load). */
  others: OfferInputs[] | null;
  budgetPct: number;
  onSaveBudget: (pct: number) => Promise<void>;
}

export default function OfferResultsPanel({ inputs, results: r, others, budgetPct, onSaveBudget }: Props) {
  const years = inputs.years;
  const y1 = r.rows[0];
  return (
    <div className="amb-results">
      {r.issues.length > 0 && (
        <div className="amb-issues" role="status">
          <b>Still needed:</b> {r.issues.join(", ")}.
        </div>
      )}

      <section className="adm-card amb-card">
        <h3 className="adm-section-title">Market value of the promotion</h3>
        <div className="amb-commit">
          <div>
            <span>Per quarter</span>
            <b>{posts(inputs.reelsPerQuarter, inputs.storiesPerQuarter)}</b>
          </div>
          <div>
            <span>Per year</span>
            <b>{posts(r.reelsPerYear, r.storiesPerYear)}</b>
          </div>
          <div>
            <span>Over {years} {years === 1 ? "year" : "years"}</span>
            <b>{posts(r.totalReels, r.totalStories)}</b>
          </div>
        </div>
        <table className="amb-formula">
          <tbody>
            <tr>
              <td>
                Reels: {count(r.reelsPerYear)} a year × {count(inputs.reelViews)} views ÷ 1,000 × {cpm(inputs.reelCpm)} CPM
              </td>
              <td>{usd(r.annualReelValue)}</td>
            </tr>
            <tr>
              <td>
                Stories: {count(r.storiesPerYear)} a year × {count(inputs.storyViews)} views ÷ 1,000 × {cpm(inputs.storyCpm)} CPM
              </td>
              <td>{usd(r.annualStoryValue)}</td>
            </tr>
            <tr className="amb-formula-step">
              <td>Annual market value</td>
              <td>{usd(r.annualMarketValue)}</td>
            </tr>
            <tr className="amb-formula-step">
              <td>
                × {times(r.strategicMultiplier)} strategic influence{" "}
                {r.strategicManual ? "(set by hand)" : r.strategicScore !== null ? `(score ${r.strategicScore.toFixed(2)}/10)` : ""} = annual
                partnership value
              </td>
              <td>{usd(r.annualPartnershipValue)}</td>
            </tr>
            <tr className="amb-formula-step">
              <td>× {years} {years === 1 ? "year" : "years"} = partnership market value</td>
              <td>{usd(r.partnershipMarketValue)}</td>
            </tr>
            <tr className="amb-formula-total">
              <td>× {times(inputs.riskPremium)} early-stage risk premium = target compensation</td>
              <td>{usd(r.targetCompensation)}</td>
            </tr>
          </tbody>
        </table>
        <p className="amb-note">
          An estimate from the assumed CPMs — not a sponsorship offer, and not the DJ’s past earnings. The risk premium pays the DJ
          for taking revenue that depends on Request’s growth instead of cash now.
        </p>
      </section>

      <section className="adm-card amb-card">
        <h3 className="adm-section-title">Proposed revenue share</h3>
        <div className="amb-shares">
          {r.periods.map((p) => (
            <div key={p.start} className={`amb-share${p.manual ? " amb-share-manual" : ""}`}>
              <span className="amb-share-years">{yearsLabel(p.start, p.end)}</span>
              <span className="amb-share-pct">{pct(p.ratePct)}</span>
              <span className="amb-share-sub">
                {p.manual ? `set by hand · calculated ${pct(p.calculatedPct)}` : `${times(p.weight)} the base rate`}
              </span>
            </div>
          ))}
        </div>
        <p className="amb-note">
          {r.baseRatePct === null ? "The base rate" : `Base rate ${pct(r.baseRatePct)} of platform revenue`} × each period’s
          weight, sized so the Base case pays the {usd(r.targetCompensation)} target. No guaranteed minimum, no equity, no
          upfront payment.
        </p>
      </section>

      {others && <RevenueBudget others={others} current={inputs} budgetPct={budgetPct} onSaveBudget={onSaveBudget} />}

      <section className="adm-card amb-card">
        <h3 className="adm-section-title">
          Projected earnings over {years} {years === 1 ? "year" : "years"}
        </h3>
        <div className="amb-scenarios">
          {SCENARIOS.map((s) => (
            <div key={s.key} className={`amb-scenario${s.key === "base" ? " amb-scenario-base" : ""}`}>
              <span className="kpi-label">{s.label}</span>
              <span className="amb-scenario-total">{usd(r.totals[s.key])}</span>
              <span className="kpi-sublabel">revenue at {count(inputs.scenarioPct[s.key])}% of forecast</span>
            </div>
          ))}
        </div>
        <div className="amb-benchmark">
          <span>
            Market value <b>{usd(r.partnershipMarketValue)}</b>
          </span>
          <span>
            Target <b>{usd(r.targetCompensation)}</b>
          </span>
          <span>
            Base case pays <b>{share(r.totals.base, r.targetCompensation)}</b> of target
          </span>
        </div>
      </section>

      <section className="adm-card amb-card">
        <h3 className="adm-section-title">Year 1 check</h3>
        <div className="amb-y1">
          <div>
            <span className="kpi-label">Annual partnership value</span>
            <b>{usd(r.annualPartnershipValue)}</b>
          </div>
          <div>
            <span className="kpi-label">Year 1 earnings, Base case</span>
            <b>{usd(y1 ? y1.base : null)}</b>
          </div>
          <div>
            <span className="kpi-label">Year 1 covers</span>
            <b>{share(y1 ? y1.base : null, r.annualPartnershipValue)}</b>
          </div>
        </div>
        <p className="amb-note">
          Year 1 earnings: Conservative {usd(y1 ? y1.conservative : null)} · Upside {usd(y1 ? y1.upside : null)}.
        </p>
      </section>

      <section className="adm-card amb-card">
        <h3 className="adm-section-title">Year by year</h3>
        <div className="adm-table-wrapper amb-scroll-x">
          <table className="adm-table-light adm-table-compact amb-table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Request revenue (Base)</th>
                <th>Revenue share</th>
                <th>Conservative</th>
                <th className="amb-base-col">Base</th>
                <th>Upside</th>
              </tr>
            </thead>
            <tbody>
              {r.rows.map((row) => (
                <tr key={row.year}>
                  <td>Year {row.year}</td>
                  <td>{usd(row.baseRevenue)}</td>
                  <td>{pct(row.ratePct)}</td>
                  <td>{usd(row.conservative)}</td>
                  <td className="amb-base-col">{usd(row.base)}</td>
                  <td>{usd(row.upside)}</td>
                </tr>
              ))}
              <tr className="amb-total">
                <td>Total</td>
                <td />
                <td />
                <td>{usd(r.totals.conservative)}</td>
                <td className="amb-base-col">{usd(r.totals.base)}</td>
                <td>{usd(r.totals.upside)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="amb-note">
          All earnings projections are illustrative and depend on Request’s actual future revenue. Projected payments are not
          guaranteed.
        </p>
      </section>
    </div>
  );
}
